import { Box, Button, Grid, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useAuth } from 'context/AuthContext';
import { useNotifications } from 'context/NotificationsContext';
import { Job, JobStatus, useApproveJobMutation } from 'graphql/generated';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RejectJobPostingDialog } from '../components';
import { ArrowButton } from 'components';
import { ApplyForm } from './applyForm';
import { EasyApply } from './easyApply';
import { ExpiredJob } from './expired-job';
import { useStyle } from './job-apply.style';

type AdminActionsProps = {
    jobId: string;
    status: JobStatus;
};

const AdminActions: React.FC<AdminActionsProps> = ({ jobId, status }) => {
    const classes = useStyle();
    const { notify } = useNotifications();
    const { t } = useTranslation();

    const [approveJob] = useApproveJobMutation({
        onCompleted() {
            notify({
                message: t('jobDetails:accepted'),
                type: 'success',
            });
        },
    });

    const acceptJobPosting = () => {
        approveJob({
            variables: { job: { jobId } },
        });
    };

    switch (status) {
        case JobStatus.Blocked:
            return (
                <Grid item xs={12}>
                    <Button
                        className={classes.button}
                        fullWidth
                        variant="contained"
                        style={{
                            backgroundColor: 'rgb(255, 0, 0, 0.08)',
                            color: 'rgb(255, 0, 0)',
                        }}
                        disabled
                    >
                        {t('common:rejected')}
                    </Button>
                </Grid>
            );

        case JobStatus.Published:
            return (
                <Grid item xs={12}>
                    <Button
                        className={classes.button}
                        fullWidth
                        variant="contained"
                        style={{
                            backgroundColor: 'rgb(67, 160, 71, 0.08)',
                            color: 'rgb(67, 160, 71)',
                        }}
                        disabled
                    >
                        {t('common:accepted')}
                    </Button>
                </Grid>
            );

        default:
            return (
                <>
                    <Grid item xs={6}>
                        <Button
                            className={clsx(classes.button, classes.acceptButton)}
                            fullWidth
                            variant="contained"
                            onClick={acceptJobPosting}
                        >
                            {t('common:accept')}
                        </Button>
                    </Grid>
                    <RejectJobPostingDialog jobId={jobId} />
                </>
            );
    }
};

type JobseekerActionsProps = {
    job: Job;
};

const JobseekerActions: React.FC<JobseekerActionsProps> = ({ job }) => {
    const { t } = useTranslation();
    const { title, status, easyApply, contacts, applicationUrl } = job;
    const emails = contacts?.emails;
    if (status === JobStatus.Expired) {
        return <ExpiredJob />;
    }
    if (easyApply) {
        return <EasyApply job={job} />;
    }
    if (applicationUrl && !emails?.length) {
        return (
            <ArrowButton
                style={{
                    padding: '18px 20px',
                    fontSize: '20px',
                }}
                text={t('jobDetails:apply')}
                hasIcon={false}
                disableRipple={false}
                target={'_blank'}
                href={applicationUrl}
            />
        );
    }
    return <ApplyForm emails={emails} title={title} />;
};

type ApplyButtonProps = {
    job: Job;
};

export const ApplyButton: React.FC<ApplyButtonProps> = ({ job }) => {
    const classes = useStyle();
    const { t } = useTranslation();
    const { isEmployer, isAdmin, isJobseeker, isLoggedIn } = useAuth();

    const statusClassNameMap = {
        [JobStatus.OnModeration]: classes.statusOnModeration,
        [JobStatus.Blocked]: classes.statusBlocked,
        [JobStatus.Published]: classes.statusPublished,
        [JobStatus.Draft]: classes.statusDraft,
    };

    const statusTextMap = {
        [JobStatus.OnModeration]: t(`enum:jobStatus_${JobStatus.OnModeration}`),
        [JobStatus.Blocked]: t(`enum:jobStatus_${JobStatus.Blocked}`),
        [JobStatus.Published]: t(`enum:jobStatus_${JobStatus.Published}`),
        [JobStatus.Draft]: t(`enum:jobStatus_${JobStatus.Draft}`),
    };

    if (isAdmin && job.status === JobStatus.OnModeration) {
        return (
            <Grid container spacing={1} style={{ marginBottom: 24 }}>
                <AdminActions jobId={job.id} status={job.status} />
            </Grid>
        );
    }
    if (isJobseeker && job.status === JobStatus.Published) {
        return (
            <div className={classes.sideSection}>
                <JobseekerActions job={job} />
            </div>
        );
    }
    if (!isLoggedIn) {
        return (
            <div className={classes.sideSection}>
                <JobseekerActions job={job} />
            </div>
        );
    }
    if (isEmployer && job.status) {
        return (
            <Box className={classes.sideSection}>
                <Typography variant="body2" className={statusClassNameMap[job.status]}>
                    {statusTextMap[job.status]}
                </Typography>
            </Box>
        );
    }
    return null;
};
